import type { MaterialDto } from "@grizzly/shared";
import { Button } from "../ui/button.js";
import { SelectInput, TextInput } from "../ui/field.js";
import { useI18n } from "../../i18n/index.js";
import { Plus, Trash2 } from "lucide-react";

export interface MaterialDraft {
  materialId: string;
  plannedQuantity: string;
}

export function MaterialsEditor({
  materials,
  catalog,
  onChange,
}: {
  materials: MaterialDraft[];
  catalog: MaterialDto[];
  onChange(materials: MaterialDraft[]): void;
}) {
  const { t } = useI18n();
  const add = () => onChange([...materials, { materialId: "", plannedQuantity: "1" }]);
  const patch = (idx: number, next: MaterialDraft) =>
    onChange(materials.map((l, i) => (i === idx ? next : l)));
  const remove = (idx: number) => onChange(materials.filter((_, i) => i !== idx));

  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-ink">{t("jobs.materials.title")}</h3>
        <Button size="sm" variant="ghost" onClick={add}>
          <Plus className="h-4 w-4" aria-hidden="true" /> {t("jobs.materials.add")}
        </Button>
      </div>
      {materials.map((m, idx) => (
        <div
          key={idx}
          className="mb-2 grid items-end gap-2 rounded border border-line p-2 sm:grid-cols-3"
        >
          <SelectInput
            label={t("common.fields.material")}
            options={catalog.map((x) => ({ value: x.id, label: `${x.sku} · ${x.name}` }))}
            value={m.materialId}
            onChange={(e) => patch(idx, { ...m, materialId: e.target.value })}
          />
          <TextInput
            label={t("common.fields.plannedQuantity")}
            value={m.plannedQuantity}
            onChange={(e) => patch(idx, { ...m, plannedQuantity: e.target.value })}
          />
          <Button size="sm" variant="ghost" onClick={() => remove(idx)}>
            <Trash2 className="h-4 w-4" aria-hidden="true" /> {t("common.remove")}
          </Button>
        </div>
      ))}
    </section>
  );
}
